import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Context } from "../../../states/Store";
import EADataGrid from "../../../components/common/datatable/EADataGrid";
import { getAccountAttributesByBondType, getAccountCategorizedAttributes } from "../../../services/edge/AttributesService";
import { attributesListColumns } from "./AttributesDataTableUtils";
import DataTableButton from "../../../components/common/datatable/DataTableButtons";
import i18next from "i18next";
import AttributeCreateDialog from "./AttributeCreateDialog";
import AttributeEditDialog from "./AttributeEditDialog";
import AttributeDeleteDialog from "./AttributeDeleteDialog";
import useStyles from "../../../style/js-style/containers/administration/attributes/AttributesDataTableStyle";
import {MenuItem} from "@mui/material";
import { CountSelectedRow } from "../../../components/common/datatable/CountSelectedRow";
import EAIcon from "../../../components/common/EAIcon";
import { PrivilegeEnum, RoleBasedAccessControlContext } from "../../../services/common/RolesUtils";
import { getContextModel } from '../../../services/edge/ContexteService';
import { ContextBondTypes } from '../../../services/common/Constants';
import { useHistory } from 'react-router';
import { useGridApiRef } from '@mui/x-data-grid';

const AttributesCounter = ({bondType, attributesCount}) => {
  const { classes } = useStyles();

  return (
    <div className={classes.headerWrapper}>
      <div className={classes.headerMetaWrapper}>
        <img src={process.env.PUBLIC_URL + (bondType === "dossier" ? "/img/icons/dossier-label_grey.svg" : "/img/icons/thirdparty-label_grey.svg")} alt="" className={classes.metaLabelIcon} />
        <span className={classes.headerMetaInfo}>{attributesCount} {i18next.t("attribute")}{attributesCount > 1 && "s"}</span>
      </div>
    </div>
  )
}

function AttributesDataTable({ bondType }) {
  const history = useHistory();
  const [loader, setLoader] = useState(true);
  const [attributesList, setAttributesList] = useState([]);
  const [attributesSelectedIds, setAttributesSelectedIds] = useState([]);
  const [isReadOnlyOpen, setIsReadOnlyOpen] = useState(false);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [state, dispatch] = useContext(Context);
  const { classes } = useStyles();
  const { hasAnyOfPrivileges } = React.useContext(RoleBasedAccessControlContext);
  const contextModels = state.contextModels;
  const apiRef = useGridApiRef();

  const fetchContextModels = useCallback(async () => {
    if(bondType === 'context') {
      await getContextModel(state.account.id, dispatch);
    }
  }, [state.account.id, bondType, dispatch]);

  const fetchAttributesList = useCallback(async () => {
    try {
      setLoader(true);
      let results;

      if(!bondType) {
        results = await getAccountCategorizedAttributes(state.account.id);
      } else {
        if(bondType === "context" && contextModels) {
          const allContextAttributes = await Promise.all([
            getAccountAttributesByBondType(state.account.id, ContextBondTypes.CONTEXT, null),
            getAccountAttributesByBondType(state.account.id, ContextBondTypes.CONTEXT_THIRD_PARTY, null),
            getAccountAttributesByBondType(state.account.id, ContextBondTypes.CONTEXT_THIRD_PARTY_DEMAND, null)
          ]);
          results = allContextAttributes.reduce((acc, val) => {
            const parsedContent = val.content.map((attribute) => {
              const ctxModel = contextModels.find((c) => c.id === attribute.ctxModelId);
              return {
                ...attribute,
                modelName: ctxModel.name
              }
            })
            acc.content.push(...parsedContent);
            return acc;
          }, { content: [] });
        } else {
          results = await getAccountAttributesByBondType(state.account.id, bondType, null);
        }
      }

      // Order results by Creation Date
      let resultsOrdered = results.content.sort(
        (a, b) => new Date(b.creationDate) - new Date(a.creationDate)
      );

      setAttributesList(
        resultsOrdered.map((x) => {
          return {
            ...x,
            typeLabel: i18next.t(`attributeType.${x.type}`),
          };
        })
      );
      setLoader(false);
    } catch (error) {
      setLoader(false);
      setAttributesList([]);
    }
  }, [state.account, bondType, contextModels]);

  const openReadDialog = (attributeId) => {
    setIsReadOnlyOpen(true);
    setSelectedAttributes([
      attributesList.find((attribute) => attribute.id === attributeId),
    ]);
  };

  useEffect(() => {
    if (state.account) {
      fetchAttributesList();
    }
  }, [state.account, fetchAttributesList]);

  useEffect(() => {
    fetchContextModels();
  }, [state.account.id, fetchContextModels]);

  const openDeleteDialog = (attributesIdsList) => {
    setIsDeleteDialogOpen(true);
    setSelectedAttributes(
      attributesList.filter(
        (attribute) => attributesIdsList.indexOf(attribute.id) !== -1
      )
    );
  };

  const openEditDialog = (attributeId) => {
    setIsEditDialogOpen(true);
    setSelectedAttributes([
      attributesList.find((attribute) => attribute.id === attributeId),
    ]);
  };

  const AttributesTableButtons = () => {
    return (
      <div>
        <div className={classes.dataTableActions}>
          {!isDeleteDisabled() && (
            <MenuItem
              onClick={() => openDeleteDialog(attributesSelectedIds, "")}
              className={classes.deleteAllBtn}
            >
              <EAIcon icon={"delete_grey"} />
              {i18next.t("delete")}
            </MenuItem>
          )}
        </div>
      </div>
    );
  };

  const isCreationDisabled = () => {
    if(!bondType) {
      return !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_PARTNER_ATTRIBUTE);
    } else {
      switch (bondType) {
        case "dossier":
          return !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_DOSSIER_ATTRIBUTE);
        case "thirdparty":
          return !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_ATTRIBUTE);
        case "context":
          return !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT_ATTRIBUTE);
        default:
          return true;
      }
    }
  };

  const isDeleteDisabled = () => {
    if(!bondType) {
      return !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_DELETE_PARTNER_ATTRIBUTE);
    } else {
      switch (bondType) {
        case "dossier":
          return !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_DELETE_DOSSIER_ATTRIBUTE);
        case "thirdparty":
          return !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_DELETE_THIRD_PARTY_ATTRIBUTE);
        case "context":
          return !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_DELETE_CONTEXT_ATTRIBUTE);
        default:
          return true;
      }
    }
  };
  
  const createBtnLabel = useMemo(() => {
    let type = bondType;
    if(!bondType) type = "partner";
    if(bondType==='context') type = "contexte";

    return i18next.t("attributes.createLabel", {type: i18next.t(type).toLowerCase()})
  }, [bondType])

  return (
    <>
      {/* Add attribute */}
      <div
        style={{
          position: "absolute",
          top: "34px",
          right: "25px",
        }}
      >
        <DataTableButton
          onClickHandler={() => setIsCreateDialogOpen(true)}
          label={createBtnLabel}
          disabled={isCreationDisabled()}
        />
      </div>

      {/* DataGrid */}
      <div className={classes.dataGridWrapper}>
        <EADataGrid
          apiRef={apiRef}
          loading={loader}
          inputRows={attributesList}
          columns={
            attributesListColumns(
              openEditDialog,
              openDeleteDialog,
              openReadDialog,
              hasAnyOfPrivileges,
              bondType,
              history,
              state.account.id
            )
          }
          groupingColDef={(params) => {
            const override = {};
            if (params.fields.includes('bondType')) {
              return {
                headerName: 'Attributs',
                valueFormatter: (valueFormatterParams) => {
                  const rowNode = apiRef.current.getRowNode(
                    valueFormatterParams.id);
                  if (rowNode?.groupingField === 'bondType') {
                    return i18next.t(`attributes.column_${rowNode?.groupingKey}`);
                  }
                  return undefined;
                },
              };
            }

            return override;
          }}
          hasCustomPagination
          customSx={{
            "& .MuiDataGrid-columnHeader:nth-of-type(2)": { color: "#7E7E8F" },
          }}
          selection={{
            items: attributesSelectedIds,
            setItems: setAttributesSelectedIds,
          }}
          additionnalsLeftComponents={
            <>
              <AttributesCounter bondType={bondType} attributesCount={attributesList.length}/>
              {attributesSelectedIds.length >= 1 && (
                <>
                  <CountSelectedRow data={attributesSelectedIds} />
                  <AttributesTableButtons />
                </>
              )}
            </>
          }
          pageSize={10}
        />
      </div>

      {/* PopUp : on create */}
      {isCreateDialogOpen && (
        <AttributeCreateDialog
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onValidateSuccess={fetchAttributesList}
          bondType={bondType}
        />
      )}

      {/* PopUp : on edit */}
      {(isEditDialogOpen || isReadOnlyOpen) && (
        <AttributeEditDialog
          attribute={selectedAttributes[0]}
          isOpen={isEditDialogOpen || isReadOnlyOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            setIsReadOnlyOpen(false);
          }}
          onValidateSuccess={fetchAttributesList}
          onDeleteOpen={() => setIsDeleteDialogOpen(true)}
          bondType={bondType}
          isDeleteDisabled={isDeleteDisabled}
          readOnly={isReadOnlyOpen}
        />
      )}

      {/* PopUp : on delete */}
      {isDeleteDialogOpen && (
        <AttributeDeleteDialog
          attributesList={selectedAttributes}
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onValidateSuccess={fetchAttributesList}
          bondType={bondType}
        />
      )}
    </>
  );
}
export default AttributesDataTable;
